/**
 * Processing pipeline: iterate video frames, detect faces, blur them.
 *
 * Yields processed ImageData frames and progress info via an AsyncGenerator
 * so the caller can render frames incrementally.
 */
export class Pipeline {
  /**
   * @param {CenterFaceDetector} detector
   * @param {WebGPUBlurrer|CanvasBlurrer} blurrer
   * @param {object} opts
   * @param {number} opts.threshold   detection confidence threshold
   * @param {number} opts.blurRadius
   * @param {number} opts.sigma
   * @param {boolean} opts.pixelate
   * @param {number}  opts.pixelSize
   */
  constructor(detector, blurrer, opts = {}) {
    this.detector = detector;
    this.blurrer  = blurrer;
    this.opts     = opts;
    this._abort   = false;
  }

  abort() { this._abort = true; }

  /**
   * Pass 1 — detection only, no blur, no recording.
   * Yields { detections, index, total, msDetect } for every frame.
   * @param {VideoReader} reader
   */
  async *detectOnly(reader) {
    const total = reader.frameCount;
    let index = 0;
    for await (const rawFrame of reader.frames()) {
      if (this._abort) break;
      const t0 = performance.now();
      const detections = await this.detector.detect(rawFrame, this.opts.threshold ?? 0.4);
      yield { detections, index: index++, total, msDetect: performance.now() - t0 };
      await microtask();
    }
  }

  /**
   * Pass 2 — full pipeline (kept for single-pass use if needed).
   * @param {VideoReader} reader
   * @yields {{ frame: ImageData, index: number, total: number, facesFound: number, msDetect: number, msBlur: number }}
   */
  async *process(reader) {
    const total = reader.frameCount;
    let index = 0;

    for await (const rawFrame of reader.frames()) {
      if (this._abort) break;

      const t0 = performance.now();
      const detections = await this.detector.detect(rawFrame, this.opts.threshold ?? 0.4);
      const t1 = performance.now();

      let outFrame;
      if (detections.length > 0) {
        outFrame = await this.blurrer.blurFaces(rawFrame, detections, {
          radius:    this.opts.blurRadius ?? 21,
          sigma:     this.opts.sigma      ?? 15,
          pixelate:  this.opts.pixelate   ?? false,
          pixelSize: this.opts.pixelSize  ?? 16,
        });
      } else {
        outFrame = rawFrame;
      }
      const t2 = performance.now();

      yield {
        frame:      outFrame,
        index:      index++,
        total,
        facesFound: detections.length,
        msDetect:   t1 - t0,
        msBlur:     t2 - t1,
      };

      // Yield to the event loop every frame so the UI stays responsive
      await microtask();
    }
  }
}

const microtask = () => new Promise(r => setTimeout(r, 0));
